import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    Divider,
    Checkbox,
    FormControlLabel,
    Chip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SortIcon from "@mui/icons-material/Sort";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import TimingEventSubNav from "./TimingEventSubNav";
import PrintSortManager from "./PrintSortManager";
import { sortCategoryNames } from "../../utils/categories";
import { groupBySections } from "../../utils/printSort";
import { officialStartlistPdf } from "../../utils/officialStartlist";
import { authHeader, clearToken } from "../../auth";

const METHODS = [
    { value: "topn", label: "Top N tiré au sort + reste par points croissants" },
    { value: "telemark_women", label: "Télémark Femmes (1-7 tirés + 8-10 tirés + reste par points)" },
    { value: "telemark_men", label: "Télémark Hommes (1-7 tirés + 8-15 tirés + reste par points)" },
    { value: "full_draw", label: "Tirage complet (tous au sort)" },
    { value: "two_fields", label: "Corrélation 2 champs (tri par 2 critères)" },
];

// Champs proposés pour "Tirer séparément par" : catégorie, genre, puis
// tous les champs libres des participants (inscription, association…),
// sauf ceux propres à chaque athlète — 07.10.2026.
const GROUP_FIELD_LABELS = { category: "Catégorie", gender: "Genre", association: "Association", club: "Club", nation: "Nation", birthyear: "Année" };
const NOT_GROUPABLE = ["bib", "order", "fiscode", "lastname", "firstname", "points", "transponder", "fromlist", "addedBy"];
const groupValue = (a, field) => String(a[field] ?? "").trim() || "(vide)";



// -----------------------------------------------------------------------
// LOGIQUE DE TIRAGE — pure, testée isolément avant intégration
// -----------------------------------------------------------------------
function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function pointsOf(a) {
    const p = parseFloat(a.points);
    return isNaN(p) ? Infinity : p;
}

function sortByPointsAsc(list) {
    return [...list].sort((a, b) => pointsOf(a) - pointsOf(b));
}

function compareValues(a, b) {
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a ?? "").localeCompare(String(b ?? ""));
}

// Variante 1 : les N meilleurs (points les plus bas) tirés au sort entre
// eux, puis le reste dans l'ordre croissant des points.
// Les concurrents à 0 point sont les MEILLEURS (0 = meilleur) : ils font
// partie des N meilleurs et sont tirés au sort avec eux.
function genTopNDrawn(list, n) {
    const sorted = sortByPointsAsc(list);
    const top = shuffle(sorted.slice(0, n));
    const rest = sorted.slice(n);
    return [...top, ...rest];
}

// Variantes 2/3 (présets télémark) — corrigé le 03.11.2026 :
//   groupe 1 = les 7 meilleurs aux points (0 point compris : ce sont les
//              meilleurs), TIRÉS AU SORT entre eux ;
//   groupe 2 = 8e à drawEnd-ième (femmes 8-10, hommes 8-15), tirés au sort ;
//   ensuite  = le reste par points croissants (sans points en dernier).
function genTelemarkPreset(list, drawEnd) {
    const sorted = sortByPointsAsc(list);
    const group1 = shuffle(sorted.slice(0, 7));
    const group2 = shuffle(sorted.slice(7, drawEnd));
    const rest = sorted.slice(drawEnd);
    return [...group1, ...group2, ...rest];
}

// Variante 4 : tout le groupe tiré au sort, sans notion de points.
function genFullDraw(list) {
    return shuffle(list);
}

// Variante 5 : tri par 2 champs choisis (le premier prioritaire).
function genTwoFieldSort(list, field1, field2) {
    return [...list].sort((a, b) => {
        const c1 = compareValues(a[field1], b[field1]);
        if (c1 !== 0) return c1;
        return compareValues(a[field2], b[field2]);
    });
}

// "Bibo N" — départ de la manche 2/3 basé sur le classement de la manche
// PRÉCÉDENTE : les N meilleurs de la manche précédente partent, dans
// l'ordre INVERSE de leur classement (le/les N-ème(s) partent en
// premier, le meilleur de la manche précédente part en dernier) —
// convention standard slalom/GS pour la manche 2. `previousRanking` :
// [{bib, elapsedMs}] déjà trié croissant (rang 1 = le plus rapide).
function genBiboOrder(list, previousRanking, n) {
    const topN = (previousRanking || []).slice(0, n);
    const reversed = [...topN].reverse();
    const byBib = new Map(list.map((a) => [String(a.bib), a]));
    return reversed.map((r) => byBib.get(r.bib)).filter(Boolean);
}

// Applique une méthode à une liste (déjà filtrée sur le bon groupe, le
// cas échéant) — utilisée directement, ou par sous-groupe si un
// regroupement est choisi pour la variante 1.
function applyMethod(list, method, params) {
    switch (method) {
        case "topn":
            return genTopNDrawn(list, params.topN);
        case "telemark_women":
            return genTelemarkPreset(
                list.filter((a) => !a.gender || a.gender.toUpperCase().startsWith("W") || a.gender.toUpperCase().startsWith("F")),
                10
            );
        case "telemark_men":
            return genTelemarkPreset(
                list.filter((a) => !a.gender || a.gender.toUpperCase().startsWith("M")),
                15
            );
        case "full_draw":
            return genFullDraw(list);
        case "two_fields":
            return genTwoFieldSort(list, params.field1, params.field2);
        case "bibo":
            return genBiboOrder(list, params.previousRunRanking, params.bibN);
        default:
            return list;
    }
}

// Groupes d'affichage ET du PDF selon le « Tri impression » de la course
// (config.printSort : sections définies par des critères, voir
// utils/printSort.js et PrintSortManager.jsx) — 07.10.2026.
function groupStartlist(startlist, printSort, categories) {
    return groupBySections(startlist, printSort, categories);
}

// -----------------------------------------------------------------------
// COMPOSANT
// -----------------------------------------------------------------------
export default function TimingStartList() {
    const { eventKey } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [athletes, setAthletes] = useState([]);
    const [startlist, setStartlist] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");

    // Manche éditée — voir en-tête ⚠️ Multi-manche (29.08.2026). `?run=`
    // dans l'URL prime ; sinon on retombe sur la manche COURANTE de
    // l'event une fois connue (runInfo), pour ne jamais éditer "au
    // hasard" une manche qu'on n'a pas explicitement choisie.
    const [runInfo, setRunInfo] = useState(null); // { currentRun, runCount, combineMethod }
    const runParam = searchParams.get("run");
    const runNumber = runParam ? Number(runParam) : runInfo?.currentRun || 1;

    // Dossards classés sur la manche PRÉCÉDENTE (bib -> vrai) — n'a de
    // sens que si runNumber > 1 ET combineMethod === "sum" (voir
    // en-tête). null tant que non pertinent/pas encore chargé.
    const [previousRunClassified, setPreviousRunClassified] = useState(null);
    // Classement (pas juste "classé ou non") de la manche précédente —
    // pour la méthode "Bibo N" (départ manche 2/3 basé sur l'ordre
    // d'arrivée de la manche précédente), indépendant du réglage
    // addition/meilleure manche.
    const [previousRunRanking, setPreviousRunRanking] = useState(null); // [{bib, elapsedMs}] trié croissant, ou null
    const [bibN, setBibN] = useState(30);

    // Générateur
    const [scope, setScope] = useState(""); // "" = tous, sinon valeur de catégorie
    const [method, setMethod] = useState("topn");
    const [topN, setTopN] = useState(15);
    const [groupField, setGroupField] = useState(""); // tirage séparé par ce champ (toutes méthodes)
    // Ordre des groupes pour le tirage (valeurs du champ choisi) et écart de
    // dossards entre deux groupes — 07.10.2026. L'ordre est mémorisé dans la
    // config de la course (groupOrders[champ]) pour être réutilisé.
    const [groupOrder, setGroupOrder] = useState([]);
    const [groupGap, setGroupGap] = useState(0);
    // 2e niveau facultatif (07.10.2026) : ex. genre puis catégorie — la
    // méthode est appliquée à chaque sous-groupe, l'écart de dossards
    // seulement entre les groupes du 1er niveau.
    const [subField, setSubField] = useState("");
    const [field1, setField1] = useState("category");
    const [field2, setField2] = useState("points");
    const [assignBibs, setAssignBibs] = useState(false);
    const [startingBib, setStartingBib] = useState(1);

    // Options du PDF (design officiel) — 01.10.2026 : l'ancienne
    // impression navigateur ("Imprimer") est supprimée, ses options passent
    // dans le PDF. Fiche de course = config de la page Course & exports.
    const [raceInfoConfig, setRaceInfoConfig] = useState(null);
    const [includeRaceInfo, setIncludeRaceInfo] = useState(true);
    // Tri impression (affichage + PDF) : config.printSort de la course.
    const printSort = raceInfoConfig?.printSort || null;

    // Catégories de la course (ordre + règles) — 01.10.2026, voir
    // CategoryManager.jsx. Stockées dans la config de la course.
    const categoryDefs = raceInfoConfig?.categories || [];
    const [showPrintSort, setShowPrintSort] = useState(false);

    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            });
            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }
            return res;
        },
        [navigate]
    );

    // ⚠️ Multi-manche (29.08.2026) : la liste de départ est propre à
    // CHAQUE manche (timing_startlist clé par event_key + run_number côté
    // serveur, voir timingStartlist.js).
    const loadStartlist = useCallback(async () => {
        const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}?run=${runNumber}`);
        if (!res) return;
        if (res.ok) {
            setStartlist(await res.json());
            setDirty(false);
        }
    }, [apiFetch, eventKey, runNumber]);

    const loadAthletes = useCallback(async () => {
        const res = await apiFetch(`/api/timing/athletes/${encodeURIComponent(eventKey)}`);
        if (!res) return;
        if (res.ok) {
            setAthletes(await res.json());
        }
    }, [apiFetch, eventKey]);

    // Manche courante/nombre de manches/méthode de combinaison.
    useEffect(() => {
        apiFetch(`/api/timing/run/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((info) => setRunInfo(info));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventKey]);

    useEffect(() => {
        loadStartlist();
        loadAthletes();
        apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((cfg) => setRaceInfoConfig(cfg));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadStartlist, loadAthletes]);

    // Résultats de la manche PRÉCÉDENTE (addition des manches) : seuls
    // les classés peuvent figurer dans le tirage de la manche en cours.
    useEffect(() => {
        if (!runInfo) return;
        if (runNumber <= 1 || runInfo.combineMethod !== "sum") {
            setPreviousRunClassified(null);
            return;
        }
        apiFetch(`/api/timing/run/${encodeURIComponent(eventKey)}/results/${runNumber - 1}`)
            .then((res) => (res && res.ok ? res.json() : []))
            .then((rows) => {
                const classified = new Set(
                    (rows || [])
                        .filter((r) => !r.status && r.elapsedMs != null)
                        .map((r) => String(r.bib))
                );
                setPreviousRunClassified(classified);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runInfo, runNumber, eventKey]);

    // Classement de la manche précédente, pour "Bibo N".
    useEffect(() => {
        if (runNumber <= 1) {
            setPreviousRunRanking(null);
            return;
        }
        apiFetch(`/api/timing/run/${encodeURIComponent(eventKey)}/results/${runNumber - 1}`)
            .then((res) => (res && res.ok ? res.json() : []))
            .then((rows) => {
                const ranked = (rows || [])
                    .filter((r) => !r.status && r.elapsedMs != null)
                    .sort((a, b) => a.elapsedMs - b.elapsedMs)
                    .map((r) => ({ bib: String(r.bib), elapsedMs: r.elapsedMs }));
                setPreviousRunRanking(ranked);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runNumber, eventKey]);

    // Vivier RÉELLEMENT disponible pour le tirage de cette manche.
    const generationPool = useMemo(() => {
        if (previousRunClassified) {
            return athletes.filter((a) => previousRunClassified.has(String(a.bib)));
        }
        return athletes;
    }, [athletes, previousRunClassified]);

    const excludedCount = athletes.length - generationPool.length;

    const switchRun = (rn) => {
        const next = new URLSearchParams(searchParams);
        next.set("run", rn);
        setSearchParams(next, { replace: true });
    };

    const categories = useMemo(() => {
        const set = new Set(generationPool.map((a) => a.category).filter(Boolean));
        return sortCategoryNames([...set], categoryDefs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generationPool, raceInfoConfig]);

    // Coché automatiquement si aucun dossard n'est encore attribué.
    const missingBibsCount = useMemo(
        () => generationPool.filter((a) => !a.bib && a.bib !== 0).length,
        [generationPool]
    );
    useEffect(() => {
        if (generationPool.length > 0 && missingBibsCount === generationPool.length) {
            setAssignBibs(true);
        }
    }, [generationPool, missingBibsCount]);

    // Suggère le prochain dossard disponible dès qu'on change de groupe.
    useEffect(() => {
        const usedBibs = [...startlist, ...athletes]
            .map((a) => Number(a.bib))
            .filter((n) => !isNaN(n));
        const maxBib = usedBibs.length > 0 ? Math.max(...usedBibs) : 0;
        setStartingBib(maxBib + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope]);

    const availableFields = useMemo(() => {
        const set = new Set();
        athletes.forEach((a) => Object.keys(a).forEach((k) => set.add(k)));
        return [...set].sort();
    }, [athletes]);

    const groupableFields = useMemo(() => {
        const rest = availableFields.filter((f) => !NOT_GROUPABLE.includes(f) && f !== "category" && f !== "gender");
        return ["category", "gender", ...rest];
    }, [availableFields]);

    // Valeurs du champ de tirage parmi les concurrents concernés (groupe
    // choisi, pas encore placés), dans l'ordre : ordre mémorisé, sinon
    // ordre des catégories définies (champ catégorie), sinon alphabétique.
    const drawCandidates = useMemo(() => {
        const athleteKey = (a) => a.fiscode || `${a.lastname}|${a.firstname}|${a.birthyear}`;
        const placed = new Set(startlist.map(athleteKey));
        const scoped = scope ? generationPool.filter((a) => a.category === scope) : generationPool;
        return scoped.filter((a) => !placed.has(athleteKey(a)));
    }, [generationPool, startlist, scope]);

    useEffect(() => {
        if (!groupField) return setGroupOrder([]);
        const present = [...new Set(drawCandidates.map((a) => groupValue(a, groupField)))];
        const saved = raceInfoConfig?.groupOrders?.[groupField] || [];
        const base =
            groupField === "category"
                ? sortCategoryNames(present, saved.length ? saved.map((name) => ({ name })) : categoryDefs)
                : [...present].sort((a, b) => {
                    const ia = saved.indexOf(a);
                    const ib = saved.indexOf(b);
                    if (ia !== -1 || ib !== -1) return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
                    return compareValues(a, b);
                });
        setGroupOrder(base);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupField, drawCandidates, raceInfoConfig]);

    const moveGroup = (i, d) =>
        setGroupOrder((prev) => {
            const t = i + d;
            if (t < 0 || t >= prev.length) return prev;
            const next = [...prev];
            [next[i], next[t]] = [next[t], next[i]];
            return next;
        });

    // Mémorise l'ordre utilisé pour ce champ dans la config de la course.
    const saveGroupOrder = async () => {
        if (!groupField || !groupOrder.length) return;
        try {
            const res = await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`);
            const cfg = (res && res.ok ? await res.json() : null) || {};
            const next = { ...cfg, groupOrders: { ...(cfg.groupOrders || {}), [groupField]: groupOrder } };
            await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`, { method: "POST", body: JSON.stringify(next) });
            setRaceInfoConfig((prev) => ({ ...(prev || {}), groupOrders: next.groupOrders }));
        } catch {
            /* non bloquant */
        }
    };

    // -----------------------------
    // Génération
    // -----------------------------
    const generate = async () => {
        setError("");
        setStatus("");

        if (generationPool.length === 0) {
            setError(
                previousRunClassified
                    ? `Aucun concurrent classé sur la manche ${runNumber - 1} — rien à faire partir sur la manche ${runNumber}.`
                    : "Aucun concurrent importé pour cet event."
            );
            return;
        }

        // Identité stable d'un concurrent (fiscode, avec repli sur
        // nom+prénom+année si absent).
        const athleteKey = (a) => a.fiscode || `${a.lastname}|${a.firstname}|${a.birthyear}`;
        const alreadyPlaced = new Set(startlist.map(athleteKey));

        const candidates = scope ? generationPool.filter((a) => a.category === scope) : [...generationPool];
        const base = candidates.filter((a) => !alreadyPlaced.has(athleteKey(a)));

        if (base.length === 0) {
            setError(
                candidates.length === 0
                    ? "Aucun concurrent dans ce groupe."
                    : "Tous les concurrents de ce groupe ont déjà une place dans la liste de départ."
            );
            return;
        }

        if (
            startlist.length > 0 &&
            !window.confirm(
                `${base.length} concurrent(s) vont être ajoutés à la suite de la liste actuelle ` +
                `(${startlist.length} déjà en place, non modifiés). Continuer ?`
            )
        ) {
            return;
        }

        const params = { topN, field1, field2, previousRunRanking: previousRunRanking, bibN };

        // Tirage séparé par groupe (champ choisi), dans l'ORDRE défini, puis
        // dossards consécutifs avec un écart éventuel entre les groupes.
        let drawGroups;
        if (groupField) {
            const order = [...groupOrder, ...[...new Set(base.map((a) => groupValue(a, groupField)))].filter((v) => !groupOrder.includes(v))];
            const subOrder = (list) => {
                const values = [...new Set(list.map((a) => groupValue(a, subField)))];
                return subField === "category" ? sortCategoryNames(values, categoryDefs) : values.sort(compareValues);
            };
            drawGroups = order
                .map((v) => {
                    const members = base.filter((a) => groupValue(a, groupField) === v);
                    if (!subField || subField === groupField) return applyMethod(members, method, params);
                    // Sous-groupes dans l'ordre (catégories : ordre défini),
                    // tirage séparé dans chacun, sans écart entre eux.
                    return subOrder(members).flatMap((sv) =>
                        applyMethod(members.filter((a) => groupValue(a, subField) === sv), method, params)
                    );
                })
                .filter((g) => g.length);
            saveGroupOrder();
        } else {
            drawGroups = [applyMethod(base, method, params)];
        }

        let nextBib = startingBib;
        const gap = Math.max(0, Number(groupGap) || 0);
        const newGroup = drawGroups.flatMap((g, gi) => {
            if (!assignBibs) return g;
            if (gi > 0) nextBib += gap;
            return g.map((a) => ({ ...a, bib: nextBib++ }));
        });

        const merged = [...startlist, ...newGroup];
        const withOrder = merged.map((a, i) => ({ ...a, order: i + 1 }));

        const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}?run=${runNumber}`, {
            method: "POST",
            body: JSON.stringify(withOrder),
        });
        if (!res) return;

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }

        setStartlist(await res.json());
        setDirty(false);
        setStatus(`Liste générée ✓ (${newGroup.length} concurrent(s) ajouté(s), ${withOrder.length} au total)`);

        // Dossards fraîchement attribués répercutés sur la liste des
        // concurrents (page Participants).
        if (assignBibs) {
            const bibByKey = new Map(newGroup.map((a) => [athleteKey(a), a.bib]));
            const updatedAthletes = athletes.map((a) =>
                bibByKey.has(athleteKey(a)) ? { ...a, bib: bibByKey.get(athleteKey(a)) } : a
            );

            await apiFetch(`/api/timing/athletes/import/${encodeURIComponent(eventKey)}`, {
                method: "POST",
                body: JSON.stringify(updatedAthletes),
            });
            setAthletes(updatedAthletes);
        }
    };

    // -----------------------------
    // Réordonnancement manuel
    // -----------------------------
    const move = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= startlist.length) return;

        const updated = [...startlist];
        [updated[index], updated[target]] = [updated[target], updated[index]];
        setStartlist(updated);
        setDirty(true);
        setStatus("");
    };

    const save = async () => {
        setError("");
        const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}?run=${runNumber}`, {
            method: "POST",
            body: JSON.stringify(startlist),
        });
        if (!res) return;

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }

        setStartlist(await res.json());
        setDirty(false);
        setStatus("Ordre sauvegardé ✓");
    };

    // =====================================================================
    // PDF "Liste de départ" (design officiel) — seule impression de la
    // page depuis le 01.10.2026. Options :
    //   - includeRaceInfo : jury + parcours (manches) en tête de document ;
    //   - groupMode : sans regroupement / par catégorie à la suite / une
    //     page par catégorie.
    // Chaque groupe garde l'ordre de départ. En-tête du tableau répété sur
    // chaque page, pagination en pied de page.
    // =====================================================================
    const generateStartlistPdf = () => {
        if (startlist.length === 0) {
            setError("Aucune liste de départ à imprimer pour cette manche.");
            return;
        }
        // Même PDF que depuis la page Chrono (utils/officialStartlistPdf.js).
        officialStartlistPdf({ config: raceInfoConfig || {}, eventKey, runNumber, startlist, includeRaceInfo, sorted: true, save: true });
    };

    // À l'écran : TOUJOURS l'ordre de départ (07.10.2026) — le regroupement
    // par catégorie ne concerne que le PDF, la catégorie est affichée en
    // colonne.
    const screenGroups = groupStartlist(startlist, printSort, categoryDefs);

    return (
        <Box p={3} maxWidth={1000}>
            <TimingEventSubNav eventKey={eventKey} current="startlist" />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h4">
                    Liste de départ — {eventKey}
                    {runInfo && runInfo.runCount > 1 ? ` — Manche ${runNumber}` : ""}
                </Typography>
            </Box>

            {runInfo && runInfo.runCount > 1 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Manche :
                    </Typography>
                    {Array.from({ length: runInfo.runCount }, (_, i) => i + 1).map((rn) => (
                        <Chip
                            key={rn}
                            label={`Manche ${rn}`}
                            size="small"
                            clickable
                            color={runNumber === rn ? "primary" : "default"}
                            variant={runNumber === rn ? "filled" : "outlined"}
                            onClick={() => switchRun(rn)}
                        />
                    ))}
                    {previousRunClassified && (
                        <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            Addition des manches : {excludedCount} concurrent(s) non classé(s) sur la manche{" "}
                            {runNumber - 1} exclu(s) du tirage.
                        </Typography>
                    )}
                </Box>
            )}

            {/* ---------- Impression : PDF (design officiel) ---------- */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    PDF de la liste de départ
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <FormControlLabel
                        control={<Checkbox checked={includeRaceInfo} onChange={(e) => setIncludeRaceInfo(e.target.checked)} />}
                        label="Inclure le jury et le parcours"
                    />
                    <Button
                        variant="contained"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={generateStartlistPdf}
                        disabled={startlist.length === 0}
                    >
                        PDF (design officiel)
                    </Button>
                    <Button variant="outlined" startIcon={<SortIcon />} onClick={() => setShowPrintSort(true)}>
                        Tri impression
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {screenGroups.length > 1 || screenGroups[0]?.category
                        ? `Tri : ${screenGroups.map((g) => `${g.category} (${g.entries.length})`).join(" → ")}` +
                        (printSort?.pageBreak ? " — une page par section" : "")
                        : "Sans tri — ordre de départ."}{" "}
                    (affichage et PDF)
                </Typography>
            </Paper>

            <PrintSortManager
                open={showPrintSort}
                onClose={() => setShowPrintSort(false)}
                eventKey={eventKey}
                startlist={startlist}
                onSaved={(ps) => {
                    setRaceInfoConfig((prev) => ({ ...(prev || {}), printSort: ps }));
                    setStatus("Tri impression enregistré ✓");
                }}
            />

            {/* ---------- Générateur ---------- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>
                    Générer une liste de départ
                </Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Groupe concerné</InputLabel>
                        <Select label="Groupe concerné" value={scope} onChange={(e) => setScope(e.target.value)}>
                            <MenuItem value="">Tous les participants</MenuItem>
                            {categories.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 340 }}>
                        <InputLabel>Méthode</InputLabel>
                        <Select label="Méthode" value={method} onChange={(e) => setMethod(e.target.value)}>
                            {METHODS.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                            {runNumber > 1 && (
                                <MenuItem value="bibo">
                                    Bibo N — reprend le classement de la manche {runNumber - 1}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Box>

                {method === "bibo" && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            type="number"
                            label={`N (meilleurs de la manche ${runNumber - 1})`}
                            value={bibN}
                            onChange={(e) => setBibN(Number(e.target.value))}
                            sx={{ width: 260 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Les N meilleurs de la manche {runNumber - 1} partent en premier — le/les {bibN}
                            {"ème(s) partent en tête, le meilleur de la manche "}
                            {runNumber - 1} part en dernier.
                        </Typography>
                    </Box>
                )}

                {method === "topn" && (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="N (meilleurs tirés au sort)"
                            value={topN}
                            onChange={(e) => setTopN(Number(e.target.value))}
                            sx={{ width: 220 }}
                        />
                    </Box>
                )}

                {/* Tirage séparé par groupe (toutes méthodes) : ordre des
                    groupes et écart de dossards entre eux — 07.10.2026. */}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "flex-start" }}>
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Tirer séparément par</InputLabel>
                        <Select label="Tirer séparément par" value={groupField} onChange={(e) => setGroupField(e.target.value)}>
                            <MenuItem value="">Aucun (tout le groupe ensemble)</MenuItem>
                            {groupableFields.map((f) => (
                                <MenuItem key={f} value={f}>
                                    {GROUP_FIELD_LABELS[f] || f}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {groupField && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>puis dans chaque groupe par</InputLabel>
                            <Select label="puis dans chaque groupe par" value={subField} onChange={(e) => setSubField(e.target.value)}>
                                <MenuItem value="">— (pas de sous-groupe)</MenuItem>
                                {groupableFields
                                    .filter((f) => f !== groupField)
                                    .map((f) => (
                                        <MenuItem key={f} value={f}>
                                            {GROUP_FIELD_LABELS[f] || f}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                    {groupField && (
                        <TextField
                            size="small"
                            type="number"
                            label="Écart de dossards entre groupes"
                            value={groupGap}
                            onChange={(e) => setGroupGap(Math.max(0, Number(e.target.value)))}
                            helperText={assignBibs ? "ex: 5 → 1-20, puis 26-…" : "actif avec l'attribution des dossards"}
                            sx={{ width: 240 }}
                        />
                    )}
                </Box>

                {groupField && groupOrder.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxWidth: 520 }}>
                        <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                            Ordre de tirage ({GROUP_FIELD_LABELS[groupField] || groupField})
                        </Typography>
                        {groupOrder.map((v, i) => {
                            const count = drawCandidates.filter((a) => groupValue(a, groupField) === v).length;
                            return (
                                <Box key={v} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ width: 24 }}>
                                        {i + 1}.
                                    </Typography>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {v} <Typography component="span" variant="caption" color="text.secondary">({count})</Typography>
                                    </Typography>
                                    <IconButton size="small" onClick={() => moveGroup(i, -1)} disabled={i === 0}>
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => moveGroup(i, 1)} disabled={i === groupOrder.length - 1}>
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {groupField === "category"
                                ? "Ordre initial = celui de « Gérer les catégories »."
                                : "Ordre mémorisé pour la course à la génération."}
                        </Typography>
                    </Paper>
                )}

                {method === "two_fields" && (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Champ 1 (prioritaire)</InputLabel>
                            <Select label="Champ 1 (prioritaire)" value={field1} onChange={(e) => setField1(e.target.value)}>
                                {availableFields.map((f) => (
                                    <MenuItem key={f} value={f}>
                                        {f}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Champ 2 (secondaire)</InputLabel>
                            <Select label="Champ 2 (secondaire)" value={field2} onChange={(e) => setField2(e.target.value)}>
                                {availableFields.map((f) => (
                                    <MenuItem key={f} value={f}>
                                        {f}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Button variant="contained" color="warning" onClick={generate}>
                    {startlist.length > 0 ? "Ajouter à la suite de la liste" : "Générer la liste de départ"}
                </Button>

                {startlist.length > 0 && (
                    <Button
                        variant="text"
                        color="error"
                        sx={{ ml: 2 }}
                        onClick={async () => {
                            if (!window.confirm("Vider entièrement la liste de départ actuelle ?")) return;
                            const res = await apiFetch(
                                `/api/timing/startlist/${encodeURIComponent(eventKey)}?run=${runNumber}`,
                                { method: "POST", body: JSON.stringify([]) }
                            );
                            if (res && res.ok) {
                                setStartlist([]);
                                setStatus("Liste vidée ✓");
                            }
                        }}
                    >
                        Vider la liste
                    </Button>
                )}

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                    control={<Checkbox checked={assignBibs} onChange={(e) => setAssignBibs(e.target.checked)} />}
                    label={
                        missingBibsCount > 0
                            ? `Attribuer les dossards automatiquement (${missingBibsCount} concurrent(s) sans dossard)`
                            : "Attribuer les dossards automatiquement (écrase les dossards existants)"
                    }
                />
                {assignBibs && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Dossard de départ"
                            value={startingBib}
                            onChange={(e) => setStartingBib(Number(e.target.value))}
                            sx={{ width: 220 }}
                        />
                    </Box>
                )}
            </Paper>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            {status && (
                <Typography color="success.main" sx={{ mb: 2 }}>
                    {status}
                </Typography>
            )}

            <Divider sx={{ mb: 3 }} />

            {startlist.length === 0 ? (
                <Typography color="text.secondary">
                    Aucune liste de départ pour l'instant — configure une méthode ci-dessus puis clique sur
                    "Générer".
                </Typography>
            ) : (
                <>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        <Button variant="contained" onClick={save} disabled={!dirty}>
                            Sauvegarder l'ordre
                        </Button>
                    </Box>

                    {/* Même regroupement que le PDF (Tri impression), ordre de départ dans chaque groupe. */}
                    {screenGroups.map((group, gi) => (
                        <Box key={gi} sx={{ mb: 3 }}>
                            {group.category && (
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {group.category} ({group.entries.length})
                                </Typography>
                            )}
                            <Paper>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Order</TableCell>
                                            <TableCell>Bib</TableCell>
                                            <TableCell>Code</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>YB</TableCell>
                                            <TableCell>NSA</TableCell>
                                            <TableCell>Cat.</TableCell>
                                            <TableCell>Club</TableCell>
                                            <TableCell>Association</TableCell>
                                            <TableCell>Points</TableCell>
                                            <TableCell align="right">Réordonner</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {group.entries.map((a) => {
                                            // Index réel dans `startlist` (pas dans le
                                            // groupe) pour que la réorganisation cible
                                            // le bon concurrent.
                                            const realIndex = startlist.indexOf(a);
                                            return (
                                                <TableRow key={`${a.bib}-${a.fiscode}-${realIndex}`}>
                                                    <TableCell sx={{ fontWeight: "bold" }}>{a.order ?? realIndex + 1}</TableCell>
                                                    <TableCell>{a.bib}</TableCell>
                                                    <TableCell>{a.fiscode}</TableCell>
                                                    <TableCell>
                                                        {a.lastname} {a.firstname}
                                                    </TableCell>
                                                    <TableCell>{a.birthyear}</TableCell>
                                                    <TableCell>{a.nation}</TableCell>
                                                    <TableCell>{a.category}</TableCell>
                                                    <TableCell>{a.club}</TableCell>
                                                    <TableCell>{a.association}</TableCell>
                                                    <TableCell>{a.points}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => move(realIndex, -1)}
                                                            disabled={realIndex === 0}
                                                        >
                                                            <ArrowUpwardIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => move(realIndex, 1)}
                                                            disabled={realIndex === startlist.length - 1}
                                                        >
                                                            <ArrowDownwardIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    ))}
                </>
            )}
        </Box>
    );
}